import { SelfContainedCommand } from '../../types/commands';
import { StandardToolResult } from '../../types/standardToolResults';
import { usePlanStore, PlanTodo } from '../../components/Space/SpaceChat/store/planStore';
import { conversationStore } from '../../components/Space/SpaceChat/store/conversationStore';
import type { CanonicalMessage } from '../../components/Space/SpaceChat/types/CanonicalMessage';

/**
 * Injects a persistent plan_card message into the conversation for a given thread.
 * Called on the first todo_write for a thread (both browser and headless paths).
 */
export function injectPlanCardMessage(threadId: string): void {
  const messageId = `plan_card_${threadId}`;
  const planCardMessage: CanonicalMessage = {
    id: messageId,
    role: 'assistant',
    threadId,
    content: [{ type: 'plan_card', threadId }],
    timestamp: new Date().toISOString(),
    // hiddenFromLlm: excluded from the history slice sent to the LLM provider.
    // This is a display-only UI element; including it would generate noisy
    // "non-Claude-native content type" warnings on every subsequent LLM call.
    metadata: { hiddenFromLlm: true },
  };
  // skipPersist=true: session-only UI message, not stored in Y.Doc or DB
  conversationStore.addMessage(threadId, planCardMessage, true);
}

export const todoWriteCommand: SelfContainedCommand = {
  id: 'todo_write',
  description: 'Create or update an in-chat task plan for tracking progress on complex tasks.',
  autonomyLevel: 'auto',

  schema: {
    parameters: [
      {
        name: 'todos',
        type: 'array',
        description: 'List of tasks with id, content, and status',
        required: true,
      },
      {
        name: 'merge',
        type: 'boolean',
        description: 'If true (default), merge with existing plan. Pass false only to replace the entire plan.',
        required: false,
      },
    ],
  },

  handler: async (params, context): Promise<StandardToolResult> => {
    let { todos = [], merge = true } = params;
    const threadId = context.threadId;

    // LLMs occasionally serialize the array as a JSON string — normalize gracefully.
    if (typeof todos === 'string') {
      try { todos = JSON.parse(todos); } catch { todos = []; }
    }

    if (!Array.isArray(todos)) {
      return {
        status: 'error',
        message: 'todos must be an array',
        error: { code: 'invalid_input', category: 'validation', details: 'todos parameter must be an array' },
        format: 'plaintext',
      };
    }

    const planStore = usePlanStore.getState();
    const existingPlan = planStore.getPlan(threadId);

    // Update plan store
    planStore.updatePlan(threadId, todos as PlanTodo[], !!merge);

    // Inject a persistent plan_card message on the first call for this thread
    if (!existingPlan?.planCardMessageId && threadId) {
      injectPlanCardMessage(threadId);
      planStore.markPlanCardInjected(threadId, `plan_card_${threadId}`);
    }

    const currentTodos = usePlanStore.getState().plans[threadId]?.todos ?? [];
    const completed = currentTodos.filter(t => t.status === 'completed').length;
    const total = currentTodos.length;

    return {
      status: 'success',
      message: `Plan updated: ${completed}/${total} tasks complete`,
      data: { todos: currentTodos, completed, total },
      format: 'json',
    };
  },
};

export const todoReadCommand: SelfContainedCommand = {
  id: 'todo_read',
  description: 'Read the current in-chat task plan for this thread.',
  autonomyLevel: 'auto',

  schema: {
    parameters: [],
  },

  handler: async (_params, context): Promise<StandardToolResult> => {
    const threadId = context.threadId;
    const planStore = usePlanStore.getState();
    const plan = planStore.getPlan(threadId);
    const todos = plan?.todos ?? [];

    const completed = todos.filter(t => t.status === 'completed').length;
    const inProgress = todos.filter(t => t.status === 'in_progress').length;
    const pending = todos.filter(t => t.status === 'pending').length;
    const total = todos.length;

    return {
      status: 'success',
      message: `Updated ${total} todo(s): ${completed} completed, ${inProgress} in progress, ${pending} pending`,
      data: { todos, completed, in_progress: inProgress, pending, total },
      format: 'json',
    };
  },
};

/**
 * plan_update command — handles SSE UI commands pushed by the headless agent loop.
 * Called by SpacePageWrapper when the tool-runtime emits a plan_update command.
 */
export const planUpdateCommand: SelfContainedCommand = {
  id: 'plan_update',
  description: 'Internal: receive a plan update from a headless agent run.',
  autonomyLevel: 'auto',

  schema: {
    parameters: [
      { name: 'threadId', type: 'string', description: 'Thread ID', required: true },
      { name: 'todos', type: 'array', description: 'Updated todos list', required: true },
      { name: 'merge', type: 'boolean', description: 'Merge or replace', required: false },
    ],
  },

  handler: async (params): Promise<StandardToolResult> => {
    const { threadId, merge = true } = params;
    let { todos = [] } = params;

    // LLMs occasionally serialize the array as a JSON string — normalize gracefully.
    if (typeof todos === 'string') {
      try { todos = JSON.parse(todos); } catch { todos = []; }
    }

    if (!threadId || !Array.isArray(todos)) {
      return {
        status: 'error',
        message: 'threadId and todos are required',
        error: { code: 'invalid_input', category: 'validation', details: 'Missing threadId or todos' },
        format: 'plaintext',
      };
    }

    const planStore = usePlanStore.getState();
    const existingPlan = planStore.getPlan(threadId);

    planStore.updatePlan(threadId, todos as PlanTodo[], !!merge);

    if (!existingPlan?.planCardMessageId) {
      injectPlanCardMessage(threadId);
      planStore.markPlanCardInjected(threadId, `plan_card_${threadId}`);
    }

    return { status: 'success', message: 'Plan updated', format: 'json' };
  },
};
